import { clampRange, dropShortRanges, normalizeRanges, range, subtractRanges, type PlayRange, type TimeRange } from '../time/ranges';
import { applySpeeds } from './speed';
import type { EditRecipe } from './recipe';

/** Pieces shorter than this are visual glitches rather than content, so they are dropped. */
export const MIN_SEGMENT_SEC = 0.08;

/** What the analyses found that a recipe may remove; each applies only when its setting is on. */
export interface Removals {
  readonly silences?: readonly TimeRange[];
  /** Hesitation sounds ("ııı", "eee"). */
  readonly fillers?: readonly TimeRange[];
  /** Moments near private places the recipe says to cut. */
  readonly privateCuts?: readonly TimeRange[];
}

/**
 * Resolves the recipe into the list of source ranges that make up the output,
 * in playback order, each with the speed it plays at. Hand-made cuts (and
 * retakes) always apply. An empty result means the edit removed everything.
 */
export function computeKeepRanges(durationSec: number, recipe: EditRecipe, { silences = [], fillers = [], privateCuts = [] }: Removals = {}): PlayRange[] {
  if (!(durationSec > 0)) return [];
  const full = range(0, durationSec);
  const base = recipe.trim ? clampRange(recipe.trim, full) : full;
  if (!base) return [];
  const cuts = [...recipe.cuts, ...(recipe.silence.enabled ? silences : []), ...(recipe.fillers.enabled ? fillers : []), ...privateCuts];
  return applySpeeds(dropShortRanges(subtractRanges([base], cuts), MIN_SEGMENT_SEC), recipe.speeds ?? []);
}

/**
 * The part to remove by hand between two moments, in either order; null when
 * it is only a sliver (a double tap, not an edit).
 */
export function manualCut(from: number, to: number, durationSec: number): TimeRange | null {
  const cut = clampRange(range(Math.min(from, to), Math.max(from, to)), range(0, durationSec));
  return cut && cut.end - cut.start >= MIN_SEGMENT_SEC ? cut : null;
}

/** Adds a cut; touching or overlapping cuts merge, so the "removed parts" list stays tidy. */
export const addCut = (cuts: readonly TimeRange[], cut: TimeRange): TimeRange[] => normalizeRanges([...cuts, cut]);
